import { Box, useTheme, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useMemo, useState, type FC, ChangeEvent } from "react";
import {
  cancelTask,
  deleteTask,
  getFetch,
  getTaskDetails,
} from "../../utils/http.util";
import {
  ERR_MSG_GET_USER_HISTORY,
  ERR_MSG_NO_TASK_ID,
  ERR_MSG_NOT_LOGIN,
  GALLERY_HISTORY_ROWS_PER_PAGE_DEFAULT,
  INFER_TASK_STATUS_ENUM,
  INFER_TASK_STATUS_HISTORY_ENUM,
  INFER_TASK_TYPE_DISPLAY_ENUM,
  INFER_TASK_TYPE_ENUM,
} from "../../utils/constants";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { PreviewModal } from "../modals/PreviewModal";
import { base64ToImgUrl } from "../../utils/image.util";
import { CustomPagination } from "./my-gallery/CustomPagination";
import { NoTaskTextTip } from "./my-gallery/NoTaskTextTip";
import { TaskActions } from "./my-gallery/TaskActions";
import { LoadingPage } from "./my-gallery/LoadingPage";

type GalleryHistoryDataType = {
  ctime: string;
  tid: string;
  type: string;
  state: string;
  utime: string;
};

/**
 *
 * @param type
 * @returns
 */
const getTaskTypeDisplay = (type: string) => {
  switch (type) {
    case INFER_TASK_TYPE_ENUM.editWithPrompt: {
      return INFER_TASK_TYPE_DISPLAY_ENUM.editWithPrompt;
    }
    case INFER_TASK_TYPE_ENUM.imageToVideo: {
      return INFER_TASK_TYPE_DISPLAY_ENUM.imageToVideo;
    }
    case INFER_TASK_TYPE_ENUM.replaceAny: {
      return INFER_TASK_TYPE_DISPLAY_ENUM.replaceAny;
    }
    case INFER_TASK_TYPE_ENUM.replaceWithReference: {
      return INFER_TASK_TYPE_DISPLAY_ENUM.replaceWithReference;
    }
    case INFER_TASK_TYPE_ENUM.segmentAny: {
      return INFER_TASK_TYPE_DISPLAY_ENUM.segmentAny;
    }
    default: {
      return "";
    }
  }
};

/**
 *
 * @param state
 * @returns
 */
const getTxtColorByTaskState = (state: string) => {
  switch (state) {
    case INFER_TASK_STATUS_ENUM.down: {
      return "success";
    }
    case INFER_TASK_STATUS_ENUM.waiting:
    case INFER_TASK_STATUS_ENUM.canceled: {
      return "warning";
    }
    case INFER_TASK_STATUS_ENUM.failed: {
      return "error";
    }
    default: {
      return "textDisabled";
    }
  }
};

/**
 *
 * @param state
 * @returns
 */
const getTxtDisplayByTaskState = (state: string) => {
  switch (state) {
    case INFER_TASK_STATUS_ENUM.down: {
      return INFER_TASK_STATUS_HISTORY_ENUM.down;
    }
    case INFER_TASK_STATUS_ENUM.waiting: {
      return INFER_TASK_STATUS_HISTORY_ENUM.waiting;
    }
    case INFER_TASK_STATUS_ENUM.canceled: {
      return INFER_TASK_STATUS_HISTORY_ENUM.canceled;
    }
    case INFER_TASK_STATUS_ENUM.failed: {
      return INFER_TASK_STATUS_HISTORY_ENUM.failed;
    }
    case INFER_TASK_STATUS_ENUM.inProgress: {
      return INFER_TASK_STATUS_HISTORY_ENUM.inProgress;
    }
    default: {
      return "";
    }
  }
};

// -------------------------------------------------------------------------------------------------------------
type MyGalleryBlockProps = {
  showPromptMsg: (msg: string) => void;
};

export const MyGalleryBlock: FC<MyGalleryBlockProps> = ({ showPromptMsg }) => {
  const [galleryHistory, setGalleryHistory] = useState<
    GalleryHistoryDataType[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: GALLERY_HISTORY_ROWS_PER_PAGE_DEFAULT,
    page: 0,
  });
  const [total, setTotal] = useState(0);

  const [showMagicPreviewModal, setShowMagicPreviewModal] = useState(false);
  const [originImgPath, setOriginImgPath] = useState("");
  const [magicFilePath, setMagicFilePath] = useState("");
  const [previewType, setPreviewType] = useState(
    INFER_TASK_TYPE_ENUM.replaceAny
  );
  const [curTaskId, setCurTaskId] = useState("");
  const [taskDetailsGetting, setTaskDetailsGetting] = useState(false);
  const [taskDeleting, setTaskDeleting] = useState(false);
  const [taskCanceling, setTaskCanceling] = useState(false);

  // responsive UI
  const theme = useTheme();
  const notPhone = useMediaQuery(theme.breakpoints.up("sm"));

  const handleGetGalleryHistoryData = (response: any) => {
    console.log(
      " -> handleGetGalleryHistoryData, data:",
      response,
      ", loading:",
      loading
    );
    setLoading(false);

    if (response?.data?.history && response.data.history?.length > 0) {
      setTotal(response.data?.total ?? 0);
      setGalleryHistory(response.data.history);
    }

    if (response?.code > 0 || response?.errCode) {
      showPromptMsg(`${response?.code || response?.errCode}: ${response?.msg}`);
    }
  };

  const handleGetGalleryHistoryCatchErr = (err: any) => {
    console.log(" -> handleGetGalleryHistoryCatchErr, err:", err);
    setLoading(false);
    setTaskDeleting(false);
    setTaskCanceling(false);
    showPromptMsg(ERR_MSG_GET_USER_HISTORY);
  };

  useEffect(() => {
    console.log(
      " -> MyGalleryBlock-useEffect, paginationModel:",
      paginationModel
    );
    setLoading(true);

    const start = paginationModel.page * paginationModel.pageSize;
    const count = paginationModel.pageSize;
    const api = `/aigc/api/gallery/history?start=${start}&count=${count}`;
    console.log(" -> MyGalleryBlock-useEffect, api:", api);

    getFetch(
      api,
      handleGetGalleryHistoryData,
      handleGetGalleryHistoryCatchErr,
      () => showPromptMsg(ERR_MSG_NOT_LOGIN)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleRowCancelTaskClick = async (tid: string) => {
    console.log("-> handleRowCancelTaskClick, taskId:", tid);

    if (tid) {
      setTaskCanceling(true);
      const data = await cancelTask(tid);
      if (data === ERR_MSG_NOT_LOGIN) {
        showPromptMsg(ERR_MSG_NOT_LOGIN);
      } else if (typeof data === "string" && data.includes(":")) {
        showPromptMsg(data);
      } else {
        setLoading(true);
        const start = paginationModel.page * paginationModel.pageSize;
        const count = paginationModel.pageSize;
        const api = `/aigc/api/gallery/history?start=${start}&count=${count}`;
        console.log(" -> handleRowCancelTaskClick, api:", api);
        getFetch(
          api,
          handleGetGalleryHistoryData,
          handleGetGalleryHistoryCatchErr,
          () => showPromptMsg(ERR_MSG_NOT_LOGIN)
        );
        showPromptMsg(`取消任务成功`);
      }
      setTaskCanceling(false);
    } else {
      showPromptMsg(ERR_MSG_NO_TASK_ID);
    }
  };

  const handleRowDeleteTaskClick = async (tid: string) => {
    console.log("-> handleRowDeleteTaskClick, taskId:", tid);

    if (tid) {
      setTaskDeleting(true);
      const data = await deleteTask(tid);
      if (data === ERR_MSG_NOT_LOGIN) {
        showPromptMsg(ERR_MSG_NOT_LOGIN);
      } else if (typeof data === "string" && data.includes(":")) {
        showPromptMsg(data);
      } else {
        setLoading(true);
        const start = paginationModel.page * paginationModel.pageSize;
        const count = paginationModel.pageSize;
        const api = `/aigc/api/gallery/history?start=${start}&count=${count}`;
        console.log(" -> handleRowDeleteTaskClick, api:", api);
        getFetch(
          api,
          handleGetGalleryHistoryData,
          handleGetGalleryHistoryCatchErr,
          () => showPromptMsg(ERR_MSG_NOT_LOGIN)
        );
        showPromptMsg(`删除任务成功`);
      }
      setTaskDeleting(false);
    } else {
      showPromptMsg(ERR_MSG_NO_TASK_ID);
    }
  };

  const handleRowGetTaskDetails = async (tid: string) => {
    console.log("-> handleRowGetTaskDetails, taskId:", tid);
    setTaskDetailsGetting(true);

    if (tid) {
      setCurTaskId(tid);
      const resp = await getTaskDetails(tid);
      if (resp === ERR_MSG_NOT_LOGIN) {
        showPromptMsg(ERR_MSG_NOT_LOGIN);
      } else if (typeof resp === "string" && resp.includes(":")) {
        showPromptMsg(resp);
      } else {
        const originImgUrl = resp?.data?.request?.init_image
          ? base64ToImgUrl(resp.data.request.init_image)
          : "";
        const magicFileUrl =
          resp?.data?.response?.data &&
          typeof resp.data.response.data[0] === "string"
            ? resp.data.response.data[0]
            : "";
        const curPreviewType = resp?.data?.type || "";
        if (originImgUrl && magicFileUrl) {
          setShowMagicPreviewModal(true);
          setOriginImgPath(originImgUrl);
          setMagicFilePath(magicFileUrl);
          setPreviewType(curPreviewType);
        } else if (magicFileUrl) {
          setShowMagicPreviewModal(true);
          setOriginImgPath("");
          setMagicFilePath(magicFileUrl);
          setPreviewType(curPreviewType);
        } else {
          showPromptMsg(`至少需要获取到魔法图/视频`);
          setCurTaskId("");

          setShowMagicPreviewModal(false);
          setOriginImgPath("");
          setMagicFilePath("");
          setPreviewType(INFER_TASK_TYPE_ENUM.replaceAny);
        }
      }

      setTaskDetailsGetting(false);
    } else {
      showPromptMsg(ERR_MSG_NO_TASK_ID);
      setTaskDetailsGetting(false);
    }
  };

  const handlePageChange = (newPage: number) =>
    setPaginationModel((prevPaginationModal) => ({
      pageSize: prevPaginationModal.pageSize,
      page: newPage,
    }));

  const handleRowsPerPageChange = (e: ChangeEvent<HTMLInputElement>) =>
    setPaginationModel((prevPaginationModal) => ({
      pageSize: parseInt(e.target.value, 10),
      page: prevPaginationModal.page,
    }));

  const handlePreviewModalClose = () => {
    setCurTaskId("");
    setShowMagicPreviewModal(false);
    setPreviewType(INFER_TASK_TYPE_ENUM.replaceAny);
  };

  const columns = useMemo<GridColDef<GalleryHistoryDataType>[]>(
    () => [
      {
        field: "ctime",
        headerName: "任务创建",
        width: 120,
        sortable: false,
        disableColumnMenu: true,
        resizable: false,
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "16px 0",
            }}
          >
            <Typography fontWeight="bold">{row.ctime}</Typography>
          </Box>
        ),
      },
      {
        field: "type",
        headerName: "任务类型",
        minWidth: 150,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        resizable: false,
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography noWrap marginRight="16px" fontWeight="bold">
              {getTaskTypeDisplay(row.type)}
            </Typography>
          </Box>
        ),
      },
      {
        field: "state",
        headerName: "任务状态",
        minWidth: 300,
        flex: 2,
        sortable: false,
        disableColumnMenu: true,
        resizable: false,
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Typography
              color={getTxtColorByTaskState(row.state)}
              fontWeight="bold"
            >
              {getTxtDisplayByTaskState(row.state)}
            </Typography>
          </Box>
        ),
      },
      {
        field: "action",
        headerName: "",
        minWidth: 300,
        flex: 2,
        sortable: false,
        disableColumnMenu: true,
        resizable: false,
        renderCell: ({ row }) => (
          <TaskActions
            taskState={row.state}
            handleCancelClick={async () =>
              await handleRowCancelTaskClick(row.tid)
            }
            handleDeleteClick={async () =>
              await handleRowDeleteTaskClick(row.tid)
            }
            handleReviewClick={async () =>
              await handleRowGetTaskDetails(row.tid)
            }
            isChosenTask={row.tid === curTaskId}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [galleryHistory, curTaskId]
  );

  return notPhone ? (
    <Box
      sx={{
        height: "calc(100vh - 130px)",
        width: "100%",
      }}
    >
      <DataGrid
        loading={loading}
        getRowId={(row) => row.tid}
        rows={galleryHistory}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [
              {
                field: "ctime",
                sort: "desc",
              },
            ],
          },
        }}
        getRowHeight={() => "auto"}
        columnHeaderHeight={48}
        disableRowSelectionOnClick={true}
        rowCount={total}
        paginationMode="server"
        sx={{
          margin: notPhone ? "0 16px" : 0,
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 800,
            fontSize: "16px",
            color: theme.palette.secondary.main,
          },
        }}
        slots={{
          noRowsOverlay: () => (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <NoTaskTextTip />
            </Box>
          ),
          pagination: () => (
            <CustomPagination
              count={total}
              page={paginationModel.page}
              onPageChange={handlePageChange}
              rowsPerPage={paginationModel.pageSize}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          ),
        }}
      />

      <LoadingPage
        showing={taskDetailsGetting || taskDeleting || taskCanceling || loading}
      />

      <PreviewModal
        open={showMagicPreviewModal}
        originFileUrl={originImgPath}
        magicFileUrl={magicFilePath}
        onModalClose={handlePreviewModalClose}
        type={previewType}
      />
    </Box>
  ) : (
    <Box
      sx={{
        height: "auto",
        width: "100%",
        paddingBottom: "32px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      {galleryHistory.length < 1 && <NoTaskTextTip />}

      {galleryHistory.length > 0 &&
        galleryHistory.map((gh, idx) => (
          <Box
            key={`gh-${idx}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: "8px",
              border: `1px solid lightgray`,
              padding: "8px",
              background:
                curTaskId === gh.tid ? theme.palette.grey[200] : "white",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Typography variant="h6" color="primary" fontWeight={800}>
                {gh.ctime}
              </Typography>

              <Typography variant="body1" fontWeight={800} fontStyle="italic">
                {getTaskTypeDisplay(gh.type)}
              </Typography>
            </Box>

            <Typography
              variant="body1"
              fontWeight={800}
              color={getTxtColorByTaskState(gh.state)}
            >
              {getTxtDisplayByTaskState(gh.state)}
            </Typography>

            <TaskActions
              taskState={gh.state}
              handleCancelClick={async () =>
                await handleRowCancelTaskClick(gh.tid)
              }
              handleDeleteClick={async () =>
                await handleRowDeleteTaskClick(gh.tid)
              }
              handleReviewClick={async () =>
                await handleRowGetTaskDetails(gh.tid)
              }
            />
          </Box>
        ))}

      <CustomPagination
        count={total}
        page={paginationModel.page}
        onPageChange={handlePageChange}
        rowsPerPage={paginationModel.pageSize}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <LoadingPage
        showing={taskDetailsGetting || taskDeleting || taskCanceling || loading}
      />

      <PreviewModal
        open={showMagicPreviewModal}
        originFileUrl={originImgPath}
        magicFileUrl={magicFilePath}
        onModalClose={handlePreviewModalClose}
        type={previewType}
      />
    </Box>
  );
};
